/**
 * Seat plans: where each pool finishing position sits in a tier's first round.
 *
 * Built once at generation and stored on `stage.settings`, so the bracket can
 * label empty seats ("Pool 1 · #3") and the runtime can seat players without
 * recomputing — one source of truth, no engine/runtime drift.
 */
import type { DrawOptions } from '../types';
import { resolveAdvancementTiers, tierDrawSize, type ResolvedTier } from './advancementTiers';
import { applySeparationLogic, calculateQualifyingPoolAdvancements } from './qualifyingAdvancement';

/** `reseat_blocked` reason code: a pool correction was refused because a withdrawn
 *  player's seat keeps an occupant whom the same correction also moves elsewhere (NB-2).
 *  The admin resolves the withdrawal (lucky loser), not a later match. Shared by the
 *  planner (`drawModel`) and the admin modal, so it lives in this pure module. */
export const WITHDRAWN_OCCUPANT_REASON = 'withdrawn_occupant';
/** `reseat_blocked` reason code: the seat was kept for a lucky loser (a flagged slot or an
 *  assigned lucky loser) whom the same correction also moves elsewhere. The admin resolves
 *  that lucky-loser seat first. */
export const LUCKY_LOSER_OCCUPANT_REASON = 'lucky_loser_occupant';

export interface SeatPlanEntry {
  pool: number; // 1-based pool number
  rank: number; // 0-based finishing position in the pool (absolute, not tier-local)
  matchIndex: number; // 0-based first-round match index
  position: 'player1' | 'player2';
}

export interface TierStageSettings {
  tier: number;
  placementBand: { from: number; to: number };
  seatPlan: SeatPlanEntry[];
  /** Real size of every pool (index 0 = pool 1) the plan was built from. Additive: stages
   *  generated before it existed do not carry it (the runtime then derives it). */
  poolSizes: number[];
}

/**
 * Where each finishing position of `tier` sits in its first round. With `poolSizes`, a
 * `(pool, rank)` the pool cannot fill (`rank >= poolSizes[pool - 1]`) is left out, so
 * that seat is unplanned — the generator makes it a BYE — instead of a seat that is
 * labelled for a player who does not exist and would strand whoever sits opposite.
 * Without `poolSizes` every pool is taken to be full (the uniform plan).
 */
export function buildSeatPlan(
  tier: ResolvedTier,
  numPools: number,
  drawSize: number,
  seededPlayers: number,
  poolSizes?: readonly number[],
): SeatPlanEntry[] {
  const placements = applySeparationLogic(
    calculateQualifyingPoolAdvancements({
      totalPools: numPools,
      advancementsPerPool: tier.perPool,
      seededPlayers,
      drawSize,
    }),
    drawSize,
  );
  return placements
    .map((p) => ({
      pool: p.poolNumber,
      rank: tier.rankFrom + p.playerRank,
      matchIndex: p.matchIndex,
      position: p.playerPosition,
    }))
    .filter((e) => !poolSizes || e.rank < (poolSizes[e.pool - 1] ?? 0));
}

/** How many real players a tier gets: from each pool, the ranks it covers that exist. */
export function tierEntrants(
  tier: Pick<ResolvedTier, 'rankFrom' | 'perPool'>,
  poolSizes: readonly number[],
): number {
  return poolSizes.reduce(
    (n, size) => n + Math.min(Math.max(size - tier.rankFrom, 0), tier.perPool),
    0,
  );
}

/** Every pool at `playersPerPool` — the plan a caller gets when it has no real sizes. */
function uniformPoolSizes(options: DrawOptions): number[] {
  const pools = options.qualifyingPools;
  if (!pools || pools.numPools <= 0) return [];
  return new Array<number>(pools.numPools).fill(pools.playersPerPool);
}

export interface TierPlacementBand {
  stageId: string;
  tier: number;
  from: number;
  to: number;
  /** Players who actually play this tier (main: seeded players + pool qualifiers). */
  players: number;
  /** Bracket size of the tier's first round. */
  drawSize: number;
}

/**
 * The places each tier decides, chained top to bottom. A band is as wide as the players
 * who play the tier — main: the seeded players plus the real qualifiers; a lower tier:
 * the real players at its ranks — never its bracket size (12 players in 3 pools of 4,
 * Top 2 + Bowl 2 → Main 1–6, Bowl 7–12, not 1–8 / 9–16). The generator and the tier
 * editor both read this, so the preview and the editor can never disagree.
 */
export function tierPlacementBands(
  options: DrawOptions,
  mainDrawSize: number,
  poolSizes: readonly number[] = uniformPoolSizes(options),
): TierPlacementBand[] {
  const pools = options.qualifyingPools;
  if (!pools) return [];
  let nextPlace = 1;
  return resolveAdvancementTiers(options).map((tier) => {
    const seeded = tier.index === 1 ? (options.seededPlayersCount ?? 0) : 0;
    const players = seeded + tierEntrants(tier, poolSizes);
    const band: TierPlacementBand = {
      stageId: tier.stageId,
      tier: tier.index,
      from: nextPlace,
      to: nextPlace + players - 1,
      players,
      drawSize: tier.index === 1 ? mainDrawSize : tierDrawSize(tier, pools.numPools),
    };
    nextPlace += players;
    return band;
  });
}

/**
 * Every first-round seat of a `drawSize` bracket that the plan does NOT fill, as a
 * 1-based bracket position (position p is match ⌊(p-1)/2⌋, player1 when p is odd).
 * Nobody is ever seated there, so each one must be a BYE — otherwise the player seated
 * opposite it waits for an opponent who never comes. The complement of the plan's real
 * entries (not `calculateByePositions`): separation can move an entry onto what the
 * seed order would call a BYE position.
 */
export function unplannedSeatPositions(plan: SeatPlanEntry[], drawSize: number): number[] {
  const planned = new Set(plan.map((e) => e.matchIndex * 2 + (e.position === 'player1' ? 1 : 2)));
  const out: number[] = [];
  for (let position = 1; position <= drawSize; position++) {
    if (!planned.has(position)) out.push(position);
  }
  return out;
}

/**
 * Settings for every tier stage (main included), keyed by stageId.
 * Main gets a plan only for round-robin pools, which are seated at runtime from standings:
 * the multi-advancer path runs the same pure functions, and the single-advancer path's
 * `seed = seeded + pool` lands on the same seat (parity-tested). Elimination pools reach
 * the main draw by bracket feeds, so their main stage stays unlabelled by the plan.
 * `mainSeatPlan: false` leaves main without a plan too: a round-robin main draw has no
 * seats (everyone plays everyone), so it takes any draw size, even one below
 * seeded + pools × top that no seat plan fits. Bands and the lower tiers' plans are unchanged.
 *
 * `poolSizes` are the real pool sizes (see `poolSizes` in advancementTiers.ts); every
 * plan leaves out the ranks a short pool does not have, and the sizes are stored on each
 * stage so the runtime seats from the same numbers. Omitted, every pool is taken to be
 * full at `playersPerPool` (the pre-C1 behaviour, kept for callers without a player count).
 */
export function buildTierStageSettings(
  options: DrawOptions,
  mainDrawSize: number,
  poolSizes: readonly number[] = uniformPoolSizes(options),
  { mainSeatPlan = true }: { mainSeatPlan?: boolean } = {},
): Map<string, TierStageSettings> {
  const out = new Map<string, TierStageSettings>();
  const pools = options.qualifyingPools;
  if (!pools) return out;
  const tiers = resolveAdvancementTiers(options);
  const bands = tierPlacementBands(options, mainDrawSize, poolSizes);
  tiers.forEach((tier, i) => {
    const seeded = tier.index === 1 ? (options.seededPlayersCount ?? 0) : 0;
    const seatPlan =
      tier.index === 1 && (!mainSeatPlan || pools.poolDrawType !== 'round_robin')
        ? []
        : buildSeatPlan(tier, pools.numPools, bands[i].drawSize, seeded, poolSizes);
    out.set(tier.stageId, {
      tier: tier.index,
      placementBand: { from: bands[i].from, to: bands[i].to },
      seatPlan,
      poolSizes: [...poolSizes],
    });
  });
  return out;
}
